import React, { useRef, useState, useEffect } from 'react';
import {
  FileText, Loader2, RefreshCw, Upload, Search, X, Calendar,
  Sun, Moon, Zap, FolderOpen, MapPin, Star, Clock, MessageSquare,
  Lightbulb, CheckCircle, AlertTriangle, Trash2, DatabaseBackup,
  Users, Sparkles, BarChart3, Mail, Activity
} from 'lucide-react';
import { useKB } from './KBContext';
import { useVisualTheme } from './ThemeContext';
import { CONFERENCE_OPTIONS } from './constants';
import GovernanceAdmin from './GovernanceAdmin';

// Compact stat card for the monitoring dashboard
const StatCard = ({ icon, label, value, hint }) => (
  <div className="kb-stat-card" style={{
    padding: '14px 16px',
    background: 'var(--kb-bg, rgba(0,0,0,0.02))',
    border: '1px solid var(--kb-border)',
    borderRadius: 10,
    display: 'flex', flexDirection: 'column', gap: 4,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {icon} <span>{label}</span>
    </div>
    <div style={{
      fontFamily: 'var(--kb-font-heading, var(--kb-font-body))',
      fontSize: 28, fontWeight: 600,
      color: 'var(--kb-fg)',
      lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums',
    }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    {hint && (
      <div style={{ fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)' }}>{hint}</div>
    )}
  </div>
);

const AdminPanel = () => {
  const {
    lightMode, setLightMode,
    backendVersion, queryLogs, suggestions, syncLogs, loadingAdmin,
    fetchAdminData,
    isIngesting, handleSyncNew, handleReingest,
    adminFiles, setAdminFiles, adminUploading, adminUploadProgress,
    adminCapWarning, handleAdminFileSelect, handleAdminUpload,
    uploadConference, setUploadConference, uploadYear, setUploadYear,
    duplicates, deletePassword, setDeletePassword,
    deletingDocId, findDuplicates, handleDeleteDoc,
    password
  } = useKB();

  const { visualTheme, setVisualTheme } = useVisualTheme();
  const adminFolderRef = useRef(null);
  const [importPassword, setImportPassword] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleImport = async () => {
    if (!importPassword) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await fetch(`${API_URL}/api/kb/import-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, import_password: importPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setImportResult({ success: true, ...data });
      } else {
        setImportResult({ success: false, error: data.detail || 'Import failed' });
      }
    } catch (err) {
      setImportResult({ success: false, error: err.message });
    }
    setImporting(false);
  };

  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Glue Up sync + Expertise reindex state
  const [glueupSyncing, setGlueupSyncing] = useState(false);
  const [glueupResult, setGlueupResult] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState(null);

  // Monitoring / stats state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kb/admin-stats`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('[AdminPanel] fetchStats failed', err);
    }
    setStatsLoading(false);
  };
  useEffect(() => { if (password) fetchStats(); /* eslint-disable-next-line */ }, [password]);

  // --- Email kill-switch (safety toggle) ---
  const [emailSetting, setEmailSetting] = useState(null);
  const [emailToggling, setEmailToggling] = useState(false);

  const fetchEmailSetting = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kb/admin-email-setting`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) setEmailSetting(await res.json());
    } catch (err) {
      console.error('[AdminPanel] fetchEmailSetting failed', err);
    }
  };
  useEffect(() => { if (password) fetchEmailSetting(); /* eslint-disable-next-line */ }, [password]);

  const handleToggleEmail = async (next) => {
    if (next && !window.confirm('Turn LIVE email sending ON? Members will start receiving real magic-link emails. Remember to turn it back OFF when done testing.')) {
      return;
    }
    setEmailToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/kb/admin-email-setting-update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, enabled: next }),
      });
      if (res.ok) setEmailSetting(await res.json());
    } catch (err) {
      console.error('[AdminPanel] handleToggleEmail failed', err);
    }
    setEmailToggling(false);
  };

  const handleGlueupSync = async () => {
    setGlueupSyncing(true);
    setGlueupResult(null);
    try {
      const res = await fetch(`${API_URL}/api/kb/admin-glueup-sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        setGlueupResult({ success: true, ...data });
      } else {
        setGlueupResult({ success: false, error: data.detail || 'Sync failed' });
      }
    } catch (err) {
      setGlueupResult({ success: false, error: err.message });
    }
    setGlueupSyncing(false);
  };

  const handleReindexExpertise = async () => {
    setReindexing(true);
    setReindexResult(null);
    try {
      const res = await fetch(`${API_URL}/api/kb/admin-reindex-expertise`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        setReindexResult({ success: true, ...data });
      } else {
        setReindexResult({ success: false, error: data.detail || 'Reindex failed' });
      }
    } catch (err) {
      setReindexResult({ success: false, error: err.message });
    }
    setReindexing(false);
  };

  const handleReset = async () => {
    if (!resetPassword) return;
    setResetting(true);
    setResetResult(null);
    try {
      const res = await fetch(`${API_URL}/api/kb/reset-to-export`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, reset_password: resetPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setResetResult({ success: true, ...data });
        setResetConfirm(false);
        setResetPassword('');
      } else {
        setResetResult({ success: false, error: data.detail || 'Reset failed' });
      }
    } catch (err) {
      setResetResult({ success: false, error: err.message });
    }
    setResetting(false);
  };

  return (
    <div className="kb-admin-container" data-testid="kb-admin-container">
      <div className="kb-admin-header">
        <h2>Settings & Admin</h2>
        <button className="kb-ingest-btn" onClick={fetchAdminData} disabled={loadingAdmin} data-testid="kb-admin-refresh">
          <RefreshCw size={16} className={loadingAdmin ? 'kb-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="kb-admin-sections">
        {/* Appearance */}
        <div className="kb-admin-section">
          <h3>{lightMode ? <Moon size={16} /> : <Sun size={16} />} Appearance</h3>
          <div className="kb-admin-theme-row">
            <span>Light / Dark</span>
            <button className="kb-theme-toggle-btn" onClick={() => setLightMode(!lightMode)}
              data-testid="kb-theme-toggle">
              {lightMode ? <><Moon size={14} /> Switch to Dark</> : <><Sun size={14} /> Switch to Light</>}
            </button>
          </div>
          <div className="kb-admin-theme-row" style={{ marginTop: 12 }}>
            <span>Visual Theme</span>
            <div className="kb-theme-selector" data-testid="kb-theme-selector">
              <button
                className={`kb-theme-option ${visualTheme === 'classic' ? 'active' : ''}`}
                onClick={() => setVisualTheme('classic')}
                data-testid="kb-theme-classic">
                Classic
              </button>
              <button
                className={`kb-theme-option ${visualTheme === 'modern' ? 'active' : ''}`}
                onClick={() => setVisualTheme('modern')}
                data-testid="kb-theme-modern">
                Modern
              </button>
            </div>
          </div>
        </div>

        {/* Email Sending — safety kill-switch */}
        <div className="kb-admin-section" data-testid="kb-email-switch-section">
          <h3><Mail size={16} /> Email Sending</h3>
          <p className="kb-admin-upload-desc">
            Master switch for outbound member emails (magic-link sign-ins + system alerts). Keep this
            <strong> OFF</strong> in production until you're ready — when off, sign-in links are still generated
            and visible in the server logs so you can test the full flow without sending anyone an email.
          </p>

          {emailSetting === null ? (
            <p className="kb-admin-empty">Loading email status…</p>
          ) : (
            <div
              data-testid="kb-email-switch-card"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                padding: '14px 18px', marginTop: 8, borderRadius: 10,
                background: emailSetting.enabled ? 'rgba(42,143,91,0.08)' : 'rgba(192,57,43,0.06)',
                border: `1px solid ${emailSetting.enabled ? 'rgba(42,143,91,0.35)' : 'rgba(192,57,43,0.3)'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  padding: '4px 10px', borderRadius: 999,
                  color: emailSetting.enabled ? '#1d6740' : '#9b2d20',
                  background: emailSetting.enabled ? 'rgba(42,143,91,0.16)' : 'rgba(192,57,43,0.12)',
                }} data-testid="kb-email-switch-status">
                  {emailSetting.enabled ? 'Live · Sending ON' : 'Safe · Sending OFF'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>
                  {emailSetting.enabled
                    ? 'Members receive real emails.'
                    : 'No emails are sent — links appear in logs only.'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleToggleEmail(!emailSetting.enabled)}
                disabled={emailToggling}
                data-testid="kb-email-switch-toggle"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '8px 16px', borderRadius: 999, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                  color: '#fff',
                  background: emailSetting.enabled ? '#c0392b' : '#2a8f5b',
                  opacity: emailToggling ? 0.6 : 1,
                }}
              >
                {emailToggling
                  ? <Loader2 size={14} className="kb-spin" />
                  : <Mail size={14} />}
                {emailSetting.enabled ? 'Turn Sending OFF' : 'Turn Sending ON'}
              </button>
            </div>
          )}
        </div>
        <div className="kb-admin-section">
          <h3><Zap size={16} /> Deployment Info</h3>
          {backendVersion ? (
            <div className="kb-version-card" data-testid="kb-version-info">
              <div className="kb-version-row">
                <span className="kb-version-label">Version</span>
                <span className="kb-version-value kb-version-badge">{backendVersion.version}</span>
              </div>
              <div className="kb-version-row">
                <span className="kb-version-label">Build</span>
                <span className="kb-version-value">{backendVersion.build}</span>
              </div>
              <div className="kb-version-row">
                <span className="kb-version-label">Features</span>
                <span className="kb-version-value">{backendVersion.features?.join(', ') || 'none'}</span>
              </div>
              {(() => {
                const hasMembers = backendVersion.features?.includes('members_directory');
                const isStale = !hasMembers || backendVersion.version === 'unknown';
                if (isStale) {
                  return (
                    <div className="kb-version-warning" data-testid="kb-version-outdated"
                      style={{ background: '#fdf3e7', borderLeft: '3px solid #c0392b', padding: '10px 12px', marginTop: 10, borderRadius: 4, color: '#7d2d1f', fontSize: 13 }}>
                      <strong>⚠ Stale backend detected.</strong> This deployment is missing the Member Directory feature.
                      Trigger a fresh redeploy from the Emergent platform — if version still shows below v3.0.0
                      after redeploy, the build cache is the issue and Emergent support should be contacted.
                    </div>
                  );
                }
                return (
                  <div data-testid="kb-version-fresh"
                    style={{ background: '#eef9f0', borderLeft: '3px solid #2a8f5b', padding: '10px 12px', marginTop: 10, borderRadius: 4, color: '#1d6740', fontSize: 13 }}>
                    <strong>✓ Backend is up to date.</strong> Member Directory feature available.
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className="kb-admin-empty">Loading version info...</p>
          )}
        </div>

        {/* Tech Stack */}
        <div className="kb-admin-section">
          <h3><Zap size={16} /> Under the Hood</h3>
          <div className="kb-geek-grid">
            <div className="kb-geek-card"><span className="kb-geek-key">Engine</span><span className="kb-geek-val">RAG + TF-IDF</span></div>
            <div className="kb-geek-card"><span className="kb-geek-key">LLM</span><span className="kb-geek-val">GPT-4o mini</span></div>
            <div className="kb-geek-card"><span className="kb-geek-key">Storage</span><span className="kb-geek-val">Cloudflare R2</span></div>
            <div className="kb-geek-card"><span className="kb-geek-key">Backend</span><span className="kb-geek-val">FastAPI + Mongo</span></div>
            <div className="kb-geek-card"><span className="kb-geek-key">Frontend</span><span className="kb-geek-val">React + Framer</span></div>
          </div>
        </div>

        {/* Monitoring / Stats Dashboard */}
        <div className="kb-admin-section" data-testid="kb-monitoring-section">
          <h3>
            <BarChart3 size={16} /> Monitoring & Stats
            <button
              type="button"
              onClick={fetchStats}
              disabled={statsLoading}
              data-testid="kb-stats-refresh"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: '1px solid var(--kb-border)',
                color: 'var(--kb-fg-soft, #8B96A5)',
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 999,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {statsLoading ? <Loader2 size={11} className="kb-spin" /> : <RefreshCw size={11} />}
              Refresh
            </button>
          </h3>
          <p className="kb-admin-upload-desc">
            At-a-glance health & usage. LLM budget lives in Profile → Universal Key — top up there if chats start failing.
          </p>

          {!stats ? (
            <p className="kb-admin-empty">Loading stats…</p>
          ) : (
            <>
              {/* Row 1 — Library + Members */}
              <div className="kb-stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12, marginBottom: 18,
              }}>
                <StatCard icon={<FileText size={14} />} label="Documents" value={stats.library.total_documents}
                  hint={`${stats.library.total_chunks.toLocaleString()} chunks indexed`} />
                <StatCard icon={<Users size={14} />} label="Active members" value={stats.members.active}
                  hint={`${stats.members.with_photos} with photos`} />
                <StatCard icon={<Activity size={14} />} label="Chats (24h)" value={stats.chat.queries_last_24h}
                  hint={`${stats.chat.queries_last_7d} this week · ${stats.chat.queries_total.toLocaleString()} all-time`} />
                <StatCard icon={<Users size={14} />} label="Unique sessions (7d)" value={stats.chat.unique_sessions_7d}
                  hint="Distinct conversations" />
                <StatCard icon={<Mail size={14} />} label="Magic links (7d)" value={stats.emails.magic_links_sent_last_7d}
                  hint={`${stats.emails.consume_rate}% redeemed`} />
                <StatCard icon={<Sparkles size={14} />} label="Members signed in" value={stats.members.members_signed_in_via_magic_link}
                  hint="via magic-link auth" />
              </div>

              {/* LLM Budget link */}
              <div data-testid="kb-llm-budget-card" style={{
                padding: '12px 16px',
                background: 'rgba(245,158,11,0.06)',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 8, fontSize: 13,
                color: 'var(--kb-fg)', display: 'flex',
                alignItems: 'center', justifyContent: 'space-between', gap: 12,
                marginBottom: 18,
              }}>
                <span><strong>Universal LLM Key budget</strong> — currently not exposed via API. Open your Emergent profile to check balance and enable auto top-up.</span>
                <a href="https://app.emergent.sh/profile/universal-key" target="_blank" rel="noopener noreferrer"
                  style={{
                    background: '#F59E0B', color: '#FFF', padding: '6px 12px',
                    borderRadius: 6, fontSize: 12, fontWeight: 600,
                    textDecoration: 'none', whiteSpace: 'nowrap',
                  }}>
                  Open Universal Key →
                </a>
              </div>

              {/* Recent questions */}
              {stats.chat.recent_questions?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)',
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Recent questions
                  </div>
                  <div style={{
                    display: 'grid', gap: 6,
                    background: 'var(--kb-bg-soft, rgba(0,0,0,0.02))',
                    border: '1px solid var(--kb-border)',
                    borderRadius: 8, padding: '10px 14px',
                    maxHeight: 220, overflowY: 'auto',
                  }}>
                    {stats.chat.recent_questions.slice(0, 10).map((q, i) => (
                      <div key={i} style={{
                        fontSize: 13, color: 'var(--kb-fg)',
                        padding: '4px 0', borderBottom: i < 9 ? '1px dashed var(--kb-border)' : 'none',
                        display: 'flex', justifyContent: 'space-between', gap: 12,
                      }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {q.question || '(empty)'}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)', fontVariantNumeric: 'tabular-nums' }}>
                          {q.timestamp ? new Date(q.timestamp).toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                          }) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Engagement footer */}
              <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)', flexWrap: 'wrap' }}>
                <span><strong>{stats.engagement.feedback_total}</strong> feedback votes</span>
                <span><strong>{stats.engagement.feedback_thumbs_down}</strong> thumbs-down</span>
                <span><strong>{stats.engagement.user_suggestions}</strong> user suggestions</span>
                <span><strong>{stats.ops.admin_auth_events_7d}</strong> admin events (7d)</span>
                {stats.ops.last_glueup_sync?.timestamp && (
                  <span>Last Glue Up sync: {new Date(stats.ops.last_glueup_sync.timestamp).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Members & Expertise (Glue Up sync + AI reindex) */}
        <div className="kb-admin-section" data-testid="kb-members-admin-section">
          <h3><Users size={16} /> Members & Expertise</h3>
          <p className="kb-admin-upload-desc">
            Sync the member roster from Glue Up, then rebuild AI-inferred expertise tags. The daily scheduler
            runs the sync automatically at 02:00 UTC — use these only for manual refreshes (e.g. after a deploy
            or when new documents have been added).
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <button className="kb-admin-upload-go" onClick={handleGlueupSync}
              disabled={glueupSyncing || reindexing} data-testid="kb-glueup-sync-btn">
              {glueupSyncing ? <><Loader2 size={14} className="kb-spin" /> Syncing Glue Up...</> : <><RefreshCw size={14} /> Force Glue Up Sync</>}
            </button>
            <button className="kb-admin-upload-go" onClick={handleReindexExpertise}
              disabled={glueupSyncing || reindexing} data-testid="kb-reindex-expertise-btn"
              style={{ background: '#6c5ce7' }}>
              {reindexing ? <><Loader2 size={14} className="kb-spin" /> Reindexing Expertise...</> : <><Sparkles size={14} /> Reindex Expertise</>}
            </button>
          </div>

          {glueupResult && (
            <div style={{ marginTop: 12 }}>
              {glueupResult.success ? (
                <div className="kb-duplicates-none" data-testid="kb-glueup-result-success">
                  <CheckCircle size={14} /> Glue Up sync {glueupResult.status}: fetched {glueupResult.fetched}, upserted {glueupResult.upserted}
                  {glueupResult.removed != null && `, removed ${glueupResult.removed}`}
                  {glueupResult.duration_ms != null && ` (${glueupResult.duration_ms}ms)`}
                </div>
              ) : (
                <div className="kb-sync-error-row" data-testid="kb-glueup-result-error">
                  <AlertTriangle size={14} /> {glueupResult.error}
                </div>
              )}
            </div>
          )}

          {reindexResult && (
            <div style={{ marginTop: 12 }}>
              {reindexResult.success ? (
                <div className="kb-duplicates-none" data-testid="kb-reindex-result-success">
                  <CheckCircle size={14} /> Reindexed expertise for {reindexResult.indexed ?? reindexResult.total ?? '—'} members.
                  {reindexResult.with_topics != null && ` ${reindexResult.with_topics} have topic chips.`}
                </div>
              ) : (
                <div className="kb-sync-error-row" data-testid="kb-reindex-result-error">
                  <AlertTriangle size={14} /> {reindexResult.error}
                </div>
              )}
            </div>
          )}
        </div>

        <GovernanceAdmin />

        {/* Delete Duplicates */}
        <div className="kb-admin-section">
          <h3><Trash2 size={16} /> Delete Duplicates</h3>
          <p className="kb-admin-upload-desc">Find and remove duplicate documents from the knowledge base.</p>
          <button className="kb-admin-upload-go" onClick={findDuplicates} data-testid="kb-find-duplicates">
            <Search size={14} /> Find Duplicates
          </button>
          {duplicates !== null && (
            <div className="kb-duplicates-results">
              {duplicates.length === 0 ? (
                <p className="kb-duplicates-none"><CheckCircle size={14} /> No duplicates found.</p>
              ) : (
                <>
                  <p className="kb-duplicates-count">{duplicates.length} duplicate group{duplicates.length !== 1 ? 's' : ''} found</p>
                  <div className="kb-duplicates-password">
                    <label>Delete password:</label>
                    <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
                      placeholder="Enter delete password" className="kb-edit-input" data-testid="kb-delete-password" />
                  </div>
                  {duplicates.map((group, gIdx) => (
                    <div key={gIdx} className="kb-duplicate-group">
                      <div className="kb-duplicate-group-header">
                        <FileText size={13} /> <strong>{group.filename}</strong> <span>({group.count} copies)</span>
                      </div>
                      {group.docs.map((doc, dIdx) => (
                        <div key={doc.doc_id} className="kb-duplicate-row">
                          <div className="kb-duplicate-info">
                            <span className="kb-duplicate-title">{doc.title}</span>
                            <span className="kb-duplicate-meta">
                              {doc.category || '—'} {doc.year ? `• ${doc.year}` : ''} {doc.conference ? `• ${doc.conference}` : ''}
                              {doc.edited_by ? ` • Edited by ${doc.edited_by}` : ''}
                              {doc.ingested_at ? ` • ${new Date(doc.ingested_at).toLocaleDateString()}` : ''}
                            </span>
                          </div>
                          {dIdx > 0 && (
                            <button className="kb-duplicate-delete-btn"
                              disabled={!deletePassword || deletingDocId === doc.doc_id}
                              onClick={() => handleDeleteDoc(doc.doc_id)}
                              data-testid={`kb-delete-${doc.doc_id}`}>
                              {deletingDocId === doc.doc_id ? <Loader2 size={12} className="kb-spin" /> : <Trash2 size={12} />}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Import Pre-processed Data */}
        <div className="kb-admin-section">
          <h3><RefreshCw size={16} /> Import Pre-processed Data</h3>
          <p className="kb-admin-upload-desc">Import documents and chunks that were processed in another environment. This loads pre-generated AI summaries, tags, and metadata without re-running ingestion.</p>
          <div className="kb-duplicates-password">
            <label>Import password:</label>
            <input type="password" value={importPassword} onChange={e => setImportPassword(e.target.value)}
              placeholder="Enter import password" className="kb-edit-input" data-testid="kb-import-password" />
          </div>
          <button className="kb-admin-upload-go" onClick={handleImport}
            disabled={!importPassword || importing} data-testid="kb-import-btn"
            style={{ marginTop: 8 }}>
            {importing ? <><Loader2 size={14} className="kb-spin" /> Importing...</> : <><RefreshCw size={14} /> Import Data</>}
          </button>
          {importResult && (
            <div style={{ marginTop: 12 }}>
              {importResult.success ? (
                <div className="kb-duplicates-none">
                  <CheckCircle size={14} /> Imported {importResult.imported_documents} documents and {importResult.imported_chunks} chunks.
                  {importResult.skipped_existing > 0 && <span> ({importResult.skipped_existing} already existed)</span>}
                </div>
              ) : (
                <div className="kb-sync-error-row">
                  <AlertTriangle size={14} /> {importResult.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reset to Clean Export */}
        <div className="kb-admin-section" data-testid="kb-reset-section">
          <h3><DatabaseBackup size={16} /> Reset to Clean Export</h3>
          <p className="kb-admin-upload-desc">
            <strong style={{ color: '#c0392b' }}>Destructive:</strong> Wipes all documents and chunks, then reloads the bundled JSON export (221 docs, 3539 chunks). Use this when the DB has accumulated duplicates or low-quality chunks from partial ingestions. Leaves query logs, suggestions, sync history, and feedback intact.
          </p>
          <div className="kb-duplicates-password">
            <label>Reset password:</label>
            <input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)}
              placeholder="Enter reset password" className="kb-edit-input" data-testid="kb-reset-password" />
          </div>
          {!resetConfirm ? (
            <button className="kb-admin-upload-go" onClick={() => setResetConfirm(true)}
              disabled={!resetPassword || resetting} data-testid="kb-reset-btn"
              style={{ marginTop: 8, background: '#c0392b' }}>
              <DatabaseBackup size={14} /> Wipe & Reload
            </button>
          ) : (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#c0392b' }}>Are you sure? This cannot be undone.</span>
              <button className="kb-admin-upload-go" onClick={handleReset}
                disabled={resetting} data-testid="kb-reset-confirm"
                style={{ background: '#c0392b' }}>
                {resetting ? <><Loader2 size={14} className="kb-spin" /> Resetting...</> : <><DatabaseBackup size={14} /> Yes, Wipe & Reload</>}
              </button>
              <button className="kb-admin-upload-go" onClick={() => setResetConfirm(false)}
                disabled={resetting} data-testid="kb-reset-cancel" style={{ background: '#7f8c8d' }}>
                Cancel
              </button>
            </div>
          )}
          {resetResult && (
            <div style={{ marginTop: 12 }}>
              {resetResult.success ? (
                <div className="kb-duplicates-none">
                  <CheckCircle size={14} /> Wiped {resetResult.wiped_documents} docs / {resetResult.wiped_chunks} chunks. Restored {resetResult.restored_documents} docs / {resetResult.restored_chunks} chunks.
                </div>
              ) : (
                <div className="kb-sync-error-row">
                  <AlertTriangle size={14} /> {resetResult.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upload Documents */}
        <div className="kb-admin-section">
          <h3><Upload size={16} /> Upload Documents</h3>
          <p className="kb-admin-upload-desc">Upload files or entire folders directly to the knowledge base.</p>
          <div className="kb-admin-upload-area">
            <div className="kb-admin-upload-btns">
              <label className="kb-admin-upload-btn">
                <FileText size={14} /> Choose Files
                <input type="file" multiple hidden
                  onChange={(e) => handleAdminFileSelect(e.target.files)}
                  accept=".pdf,.docx,.pptx,.ppt,.potx,.txt,.csv,.md" />
              </label>
              <label className="kb-admin-upload-btn">
                <FolderOpen size={14} /> Choose Folder
                <input type="file" hidden ref={adminFolderRef}
                  onChange={(e) => handleAdminFileSelect(e.target.files)}
                  webkitdirectory="" directory="" />
              </label>
            </div>
            {adminCapWarning && <p className="kb-admin-upload-warn">{adminCapWarning}</p>}
            {adminFiles.length > 0 && !adminUploading && (
              <div className="kb-admin-upload-ready">
                <div className="kb-admin-upload-meta">
                  <div className="kb-admin-upload-meta-field">
                    <label><MapPin size={12} /> Conference/Event</label>
                    <select value={uploadConference} onChange={e => setUploadConference(e.target.value)}
                      className="kb-edit-select" data-testid="kb-upload-conference">
                      <option value="">— Unknown —</option>
                      {CONFERENCE_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="kb-admin-upload-meta-field">
                    <label><Calendar size={12} /> Year</label>
                    <input type="number" value={uploadYear} onChange={e => setUploadYear(e.target.value)}
                      placeholder="e.g. 2024" min="1975" max="2030"
                      className="kb-edit-input kb-upload-year-input" data-testid="kb-upload-year" />
                  </div>
                </div>
                <div className="kb-admin-upload-ready-row">
                  <span>{adminFiles.length} file{adminFiles.length !== 1 ? 's' : ''} selected</span>
                  <button className="kb-admin-upload-go" onClick={handleAdminUpload}
                    data-testid="kb-admin-upload-go">
                    <Upload size={14} /> Upload All
                  </button>
                </div>
              </div>
            )}
            {adminUploading && (
              <div className="kb-admin-upload-progress">
                <Loader2 size={14} className="kb-spin" />
                <span>{adminUploadProgress.current}/{adminUploadProgress.total} — {adminUploadProgress.succeeded} ok, {adminUploadProgress.failed} failed</span>
              </div>
            )}
            {!adminUploading && adminUploadProgress.succeeded > 0 && adminFiles.length === 0 && (
              <div className="kb-sync-reminder" data-testid="kb-admin-sync-reminder">
                <RefreshCw size={16} />
                <div className="kb-sync-reminder-text">
                  <strong>Sync required</strong>
                  Uploaded files won't appear in the library until processed. Click Sync to update.
                </div>
                <button className="kb-sync-reminder-btn" onClick={handleSyncNew} disabled={isIngesting}
                  data-testid="kb-admin-sync-reminder-btn">
                  <RefreshCw size={13} /> Sync Now
                </button>
              </div>
            )}
          </div>
        </div>

        {/* What's New */}
        <div className="kb-admin-section">
          <h3><Star size={16} /> What's New</h3>
          {backendVersion?.changelog?.length > 0 ? (
            <div className="kb-changelog" data-testid="kb-changelog">
              {backendVersion.changelog.map((release, i) => (
                <div key={i} className={`kb-changelog-release ${release.is_upcoming ? 'upcoming' : i === 0 ? '' : ''} ${!release.is_upcoming && backendVersion.changelog.filter(r => !r.is_upcoming)[0]?.version === release.version ? 'latest' : ''}`} data-testid={`kb-changelog-${i}`}>
                  <div className="kb-changelog-header">
                    <span className={`kb-changelog-version ${release.is_upcoming ? 'upcoming' : ''}`}>
                      {release.is_upcoming ? 'Coming Soon' : `v${release.version}`}
                    </span>
                    {release.date && <span className="kb-changelog-date">{release.date}</span>}
                    {release.is_upcoming && <span className="kb-changelog-upcoming-badge">Roadmap</span>}
                    {!release.is_upcoming && backendVersion.changelog.filter(r => !r.is_upcoming)[0]?.version === release.version && (
                      <span className="kb-changelog-latest-badge">Latest</span>
                    )}
                  </div>
                  <ul className="kb-changelog-list">
                    {release.highlights.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="kb-admin-empty">No changelog available.</p>
          )}
        </div>

        {/* Query Logs */}
        <div className="kb-admin-section">
          <h3><Clock size={16} /> Recent Questions ({queryLogs.length})</h3>
          {queryLogs.length === 0 ? (
            <p className="kb-admin-empty">No questions logged yet.</p>
          ) : (
            <div className="kb-admin-list">
              {queryLogs.slice(0, 50).map((log, i) => (
                <div key={i} className="kb-admin-item" data-testid={`kb-log-${i}`}>
                  <div className="kb-admin-item-main">
                    <MessageSquare size={14} />
                    <span className="kb-admin-item-text">{log.question}</span>
                  </div>
                  <span className="kb-admin-item-time">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Suggestions */}
        <div className="kb-admin-section">
          <h3><Lightbulb size={16} /> User Suggestions ({suggestions.length})</h3>
          {suggestions.length === 0 ? (
            <p className="kb-admin-empty">No suggestions yet.</p>
          ) : (
            <div className="kb-admin-list">
              {suggestions.map((s, i) => (
                <div key={i} className="kb-admin-item suggestion" data-testid={`kb-sug-${i}`}>
                  <div className="kb-admin-item-main">
                    <Lightbulb size={14} />
                    <span className="kb-admin-item-text">{s.text}</span>
                  </div>
                  <span className="kb-admin-item-time">{new Date(s.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sync Logs */}
        <div className="kb-admin-section">
          <h3><RefreshCw size={16} /> Sync History ({syncLogs.length})</h3>
          {syncLogs.length === 0 ? (
            <p className="kb-admin-empty">No sync history yet.</p>
          ) : (
            <div className="kb-admin-list">
              {syncLogs.map((log, i) => (
                <div key={i} className={`kb-sync-log-item ${log.error_count > 0 ? 'has-errors' : ''}`} data-testid={`kb-sync-log-${i}`}>
                  <div className="kb-sync-log-header">
                    <span className={`kb-sync-status ${log.status}`}>
                      {log.status === 'completed' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
                      {log.status}
                    </span>
                    <span className="kb-admin-item-time">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="kb-sync-log-stats">
                    <span>{log.processed} ingested</span>
                    <span>{log.skipped} skipped</span>
                    <span>{log.total} total scanned</span>
                    {log.error_count > 0 && <span className="kb-sync-err">{log.error_count} errors</span>}
                  </div>
                  {log.error_count > 0 && log.errors?.length > 0 && (
                    <div className="kb-sync-errors">
                      {log.errors.slice(0, 5).map((err, ei) => (
                        <div key={ei} className="kb-sync-error-row">
                          <AlertTriangle size={12} />
                          <span>{err.file || 'Unknown'}: {err.error}</span>
                        </div>
                      ))}
                      {log.error_count > 5 && <span className="kb-sync-more">...and {log.error_count - 5} more</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
